import React from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutGrid, ShieldCheck, LogOut, Cloud, Shield, Workflow, CreditCard, DownloadCloud, Megaphone, Users, FolderInput } from "lucide-react";
import { LogoWordmark } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { EmieWidget } from "@/components/EmieWidget";
import { TrialBanner } from "@/components/TrialBanner";
import { NextStepNudge } from "@/components/NextStepNudge";
import { WelcomeTour } from "@/components/WelcomeTour";
import { useAuth } from "@/context/AuthContext";
import { PLANS } from "@/lib/plans";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutGrid, end: true },
  { to: "/dashboard/connections", label: "Connections", icon: Cloud, end: false },
  { to: "/dashboard/jobs", label: "Jobs", icon: Workflow, end: false },
  { to: "/dashboard/extraction", label: "Extraction", icon: DownloadCloud, end: false },
  { to: "/dashboard/attachments", label: "Attachments → Files", icon: FolderInput, end: false },
  { to: "/dashboard/governance", label: "Governance", icon: ShieldCheck, end: false },
  { to: "/dashboard/campaigns", label: "Campaigns", icon: Megaphone, end: false },
  { to: "/dashboard/team", label: "Team", icon: Users, end: false },
  { to: "/dashboard/billing", label: "Billing", icon: CreditCard, end: false },
];

export function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const planName = PLANS.find((p) => p.id === user?.plan)?.name || user?.plan;

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur">
        <div className="flex items-center justify-between px-6 py-3 lg:px-8">
          <div className="flex items-center gap-8">
            <LogoWordmark size={34} />
            <nav className="hidden items-center gap-1 md:flex">
              {NAV.map((n) => {
                const Icon = n.icon;
                return (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    className={({ isActive }) =>
                      `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                        isActive
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`
                    }
                    data-testid={`app-nav-${n.label.toLowerCase()}`}
                  >
                    <Icon className="h-4 w-4" />
                    {n.label}
                  </NavLink>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            {user?.role === "superadmin" && (
              <Link to="/admin">
                <Button variant="outline" size="sm" className="gap-2" data-testid="go-to-admin">
                  <Shield className="h-4 w-4" /> <span className="hidden sm:inline">Admin</span>
                </Button>
              </Link>
            )}
            <div className="hidden text-right sm:block">
              <p className="text-sm font-semibold leading-none" data-testid="app-user-name">
                {user?.name}
              </p>
              <p className="mt-1 text-xs text-muted-foreground" data-testid="app-user-plan">
                {planName} plan
              </p>
            </div>
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={handleLogout} className="gap-2" data-testid="logout-button">
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Log out</span>
            </Button>
          </div>
        </div>

        {/* Mobile nav */}
        <nav className="flex items-center gap-1 overflow-x-auto border-t border-border px-2 py-2 md:hidden">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                title={n.label}
                className={({ isActive }) =>
                  `flex min-w-[4.25rem] flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-2 py-1.5 text-[10px] font-semibold ${
                    isActive ? "bg-primary/10 text-primary" : "text-muted-foreground"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            );
          })}
        </nav>
      </header>

      <TrialBanner />
      <NextStepNudge />

      <main className="mx-auto max-w-6xl p-6 lg:p-10">
        <Outlet />
      </main>

      <EmieWidget />
      <WelcomeTour />
    </div>
  );
}
